import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { ChatAssistantStore } from 'stores/chat-assistant.store';
import { environment } from 'src/environments/environment';
import { ScriptLoaderService } from 'src/app/services/script-loader.service';
import { ChatAssistantService } from 'src/app/services/chat-assistant.service';
import { ChatAssistantApi } from 'src/app/api/chat-assistant.api';
import { TableStore } from 'stores/table.store';
import { Subscription, firstValueFrom, filter } from 'rxjs';
import { OrderMenuStore } from 'stores/order-menu.store';

// Declare the chatkit element type for TypeScript
declare global {
  interface Window {
    chatkit?: any;
  }
}

@Component({
  selector: 'chat-assistant-dialogue',
  templateUrl: 'chat-assistant-dialogue.component.html',
  styleUrls: ['chat-assistant-dialogue.component.scss']
})
export class ChatAssistantDialogue implements AfterViewInit, OnDestroy {
  private chatkitInitialized = false;
  private apiBaseUrl: string;
  private readonly CHATKIT_SDK_URL = 'https://cdn.platform.openai.com/deployments/chatkit/chatkit.js';
  private subscriptions: Subscription[] = [];
  private chatkitElement: any;
  
  // Drag state
  isDragging = false;
  dragStartY = 0;
  dragCurrentY = 0;
  dragStartTime = 0;
  dragTransform = 'translateY(0)';
  private mouseMoveListener?: (e: MouseEvent) => void;
  private mouseUpListener?: (e: MouseEvent) => void;
  private touchMoveListener?: (e: TouchEvent) => void;
  private touchEndListener?: (e: TouchEvent) => void;

  constructor(
    public chatAssistantStore: ChatAssistantStore,
    private scriptLoaderService: ScriptLoaderService,
    private chatAssistantService: ChatAssistantService,
    private chatAssistantApi: ChatAssistantApi,
    private tableStore: TableStore,
    private orderMenuStore: OrderMenuStore
  ) {
    this.apiBaseUrl = environment.readyConfig.serverUrl;
  }

  async ngAfterViewInit(): Promise<void> {
    // Watch for modal open/close state changes to reset drag state
    this.subscriptions.push(
      this.chatAssistantStore.chatModalIsOpen$.subscribe((isOpen) => {
        // Use setTimeout to ensure DOM is ready and any animations can complete
        setTimeout(() => {
          this.resetDragState();
        }, isOpen ? 50 : 0); // Small delay when opening to let slide-up animation start
      })
    );
    
    // Load the Chatkit SDK script and initialize
    await this.loadAndInitializeChatkit();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.cleanupDragListeners();
  }

  // Drag handlers
  onDragStart(event: TouchEvent | MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    this.isDragging = true;
    this.dragStartTime = Date.now();
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    this.dragStartY = clientY;
    this.dragCurrentY = clientY;
    
    // Prevent body scroll while dragging
    document.body.style.overflow = 'hidden';
    
    // Disable pointer events on chatkit iframe during drag to prevent event capture
    const chatkitElement = document.getElementById('my-chat');
    if (chatkitElement) {
      const iframe = chatkitElement.querySelector('iframe');
      if (iframe) {
        iframe.style.pointerEvents = 'none';
      }
      // Also disable pointer events on the chatkit element itself
      (chatkitElement as HTMLElement).style.pointerEvents = 'none';
    }
    
    // Add listeners using capture phase to catch events before iframe
    if ('touches' in event) {
      this.touchMoveListener = (e: TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.onDragMove(e);
      };
      this.touchEndListener = (e: TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.onDragEnd(e);
      };
      document.addEventListener('touchmove', this.touchMoveListener, { passive: false, capture: true });
      document.addEventListener('touchend', this.touchEndListener, { capture: true });
      document.addEventListener('touchcancel', this.touchEndListener, { capture: true });
    } else {
      this.mouseMoveListener = (e: MouseEvent) => {
        if (this.isDragging) {
          e.preventDefault();
          e.stopPropagation();
          this.onDragMove(e);
        }
      };
      this.mouseUpListener = (e: MouseEvent) => {
        if (this.isDragging) {
          e.preventDefault();
          e.stopPropagation();
          this.onDragEnd(e);
        }
      };
      document.addEventListener('mousemove', this.mouseMoveListener, { capture: true });
      document.addEventListener('mouseup', this.mouseUpListener, { capture: true });
      document.addEventListener('mouseleave', this.mouseUpListener, { capture: true });
    }
  }

  onDragMove(event: TouchEvent | MouseEvent): void {
    if (!this.isDragging) return;
    
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    const deltaY = clientY - this.dragStartY;
    
    // Only allow dragging down
    if (deltaY > 0) {
      this.dragCurrentY = clientY;
      this.dragTransform = `translateY(${deltaY}px)`;
      
      // Add opacity fade as user drags down
      const containerOpacity = Math.max(0.3, 1 - (deltaY / 300));
      const backdropOpacity = Math.max(0, 0.5 - (deltaY / 600));
      
      const chatContainer = document.querySelector('.chat-container') as HTMLElement;
      const backdrop = document.querySelector('.chat-backdrop') as HTMLElement;
      
      if (chatContainer) {
        chatContainer.style.opacity = containerOpacity.toString();
      }
      if (backdrop) {
        backdrop.style.opacity = backdropOpacity.toString();
      }
    }
  }

  onDragEnd(event: TouchEvent | MouseEvent): void {
    if (!this.isDragging) return;
    
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Restore pointer events on chatkit iframe
    const chatkitElement = document.getElementById('my-chat');
    if (chatkitElement) {
      const iframe = chatkitElement.querySelector('iframe');
      if (iframe) {
        iframe.style.pointerEvents = '';
      }
      (chatkitElement as HTMLElement).style.pointerEvents = '';
    }
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    const clientY = 'changedTouches' in event ? event.changedTouches[0].clientY : event.clientY;
    const deltaY = clientY - this.dragStartY;
    const dragDuration = Date.now() - this.dragStartTime;
    const velocity = deltaY / dragDuration; // pixels per ms
    
    const threshold = 100; // Dismiss threshold in pixels
    const velocityThreshold = 0.5; // Fast swipe threshold (pixels per ms)
    
    this.cleanupDragListeners();
    
    // Dismiss if dragged far enough OR if swiped fast enough
    const shouldDismiss = deltaY > threshold || (deltaY > 50 && velocity > velocityThreshold);
    
    if (shouldDismiss) {
      // Dismiss the chat - state will be reset by the subscription
      this.chatAssistantStore.setChatModalOpen(false);
    } else {
      // Snap back to original position
      this.dragTransform = 'translateY(0)';
      const chatContainer = document.querySelector('.chat-container') as HTMLElement;
      const backdrop = document.querySelector('.chat-backdrop') as HTMLElement;
      
      if (chatContainer) {
        chatContainer.style.opacity = '1';
      }
      if (backdrop) {
        backdrop.style.opacity = '';
      }
    }
    
    // Reset drag state
    this.isDragging = false;
    this.dragStartY = 0;
    this.dragCurrentY = 0;
    this.dragStartTime = 0;
  }

  private resetDragState(): void {
    // Reset all drag-related state variables
    this.isDragging = false;
    this.dragStartY = 0;
    this.dragCurrentY = 0;
    this.dragStartTime = 0;
    this.dragTransform = 'translateY(0)';
    
    // Use requestAnimationFrame to ensure DOM updates happen after Angular change detection
    requestAnimationFrame(() => {
      // Reset inline styles on DOM elements (these override Angular bindings)
      const chatContainer = document.querySelector('.chat-container') as HTMLElement;
      const backdrop = document.querySelector('.chat-backdrop') as HTMLElement;
      const chatkitElement = document.getElementById('my-chat');
      
      if (chatContainer) {
        chatContainer.style.opacity = '';
        chatContainer.style.transform = '';
        // Force reflow to ensure styles are cleared
        chatContainer.offsetHeight;
      }
      if (backdrop) {
        backdrop.style.opacity = '';
      }
      if (chatkitElement) {
        const iframe = chatkitElement.querySelector('iframe');
        if (iframe) {
          iframe.style.pointerEvents = '';
        }
        (chatkitElement as HTMLElement).style.pointerEvents = '';
      }
      
      // Restore body scroll
      document.body.style.overflow = '';
    });
    
    // Clean up any active listeners
    this.cleanupDragListeners();
  }

  private cleanupDragListeners(): void {
    if (this.mouseMoveListener) {
      document.removeEventListener('mousemove', this.mouseMoveListener, { capture: true });
      this.mouseMoveListener = undefined;
    }
    if (this.mouseUpListener) {
      document.removeEventListener('mouseup', this.mouseUpListener, { capture: true });
      document.removeEventListener('mouseleave', this.mouseUpListener, { capture: true });
      this.mouseUpListener = undefined;
    }
    if (this.touchMoveListener) {
      document.removeEventListener('touchmove', this.touchMoveListener, { capture: true });
      this.touchMoveListener = undefined;
    }
    if (this.touchEndListener) {
      document.removeEventListener('touchend', this.touchEndListener, { capture: true });
      document.removeEventListener('touchcancel', this.touchEndListener, { capture: true });
      this.touchEndListener = undefined;
    }
  }

  private async loadAndInitializeChatkit(): Promise<void> {
    try {
      // Load the Chatkit SDK using the script loader service
      await this.scriptLoaderService.loadScript(this.CHATKIT_SDK_URL);
      
      // Wait a bit for the SDK to initialize after loading
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Now initialize the chatkit widget
      await this.initializeChatkit();
    } catch (error) {
      console.error('Failed to load Chatkit SDK:', error);
    }
  }

  private async initializeChatkit(): Promise<void> {
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds with 100ms intervals
    
    // Check if script is loaded and element is available
    const checkScriptLoaded = setInterval(async () => {
      attempts++;
      const chatkitElement = document.getElementById('my-chat') as any;
      
      // Debug logging - check window for global chatkit objects
      if (attempts === 1 || attempts % 10 === 0) {
        console.log('Chatkit initialization attempt', attempts, {
          elementExists: !!chatkitElement,
          hasSetOptions: chatkitElement && 'setOptions' in chatkitElement
        });
      }
      
      if (chatkitElement && typeof chatkitElement.setOptions === 'function' && !this.chatkitInitialized) {
        clearInterval(checkScriptLoaded);
        this.chatkitInitialized = true;
        this.chatkitElement = chatkitElement;
        
        console.log('Chatkit element found, initializing...');
        
        try {
          // Await table data so we can configure startScreen prompts before the widget renders
          const initialTable = this.tableStore.currentTable
            ?? await firstValueFrom(this.tableStore.currentTable$.pipe(filter(t => t !== null)));
          const beaconId = initialTable?.beaconId || '';
          
          const cannedPrompts = initialTable?.location?.serverFlow?.cannedPrompts;
          const startScreenConfig = cannedPrompts?.length
            ? { prompts: cannedPrompts.map((cp: { title: string; prompt: string }) => ({ label: cp.title, prompt: cp.prompt })) }
            : undefined;
          
          // Store reference to component instance for use in callbacks
          const componentInstance = this;
          
          // Helper function to extract item ID from various possible locations
          const extractItemId = (actionData: any, item: any): string | null => {
            // Try multiple possible locations for item ID
            return actionData?.payload?.id ||
                   actionData?.payload?.itemId ||
                   actionData?.payload?.item_id ||
                   item?.id ||
                   item?.itemId ||
                   item?.item_id ||
                   item?.payload?.id ||
                   null;
          };

          // Helper function to extract item name
          const extractItemName = (actionData: any, item: any): string | undefined => {
            return actionData?.name ||
                   actionData?.payload?.name ||
                   item?.name ||
                   item?.payload?.name;
          };

          // Create the onAction callback function
          const onActionHandler = async (action: any, item: any): Promise<any> => {
            console.log('=== ChatKit widget action received ===', { 
              action, 
              item, 
              actionType: typeof action,
              actionString: JSON.stringify(action),
              itemString: JSON.stringify(item),
              timestamp: new Date().toISOString()
            });
            
            try {
              // Extract action type - handle both string and object formats
              let actionType: string;
              let actionData: any = {};
              
              if (typeof action === 'string') {
                actionType = action;
                actionData = item || {};
              } else if (typeof action === 'object' && action !== null) {
                // Action is an object, extract the type from various possible locations
                actionType = action.type || action.action || action.name || action.event || 'unknown';
                // Merge action properties with item
                actionData = { ...action, ...(item || {}) };
              } else {
                console.error('Invalid action format:', action);
                return { error: 'Invalid action format', success: false };
              }
              
              console.log('Extracted action type:', actionType);
              
              // Map widget actions to tool calls
              let toolName: string;
              let parameters: any = {};
              
              // Handle different action types and extract parameters
              switch (actionType) {
                case 'product.view':
                  // product.view should navigate to item details page
                  const viewItemId = extractItemId(actionData, item);
                  
                  if (viewItemId) {
                    console.log('product.view - navigating to item details:', viewItemId);
                    // Navigate directly to item details page
                    const result = await componentInstance.chatAssistantService.navigateToItemDetails(viewItemId);
                    if (result.success) {
                      // Close chat window
                      componentInstance.chatAssistantStore.setChatModalOpen(false);
                    }
                    return result;
                  } else {
                    console.error('product.view - no item ID found:', { actionData, item });
                    return { success: false, message: 'Item ID not found' };
                  }
                  
                case 'add_to_cart':
                case 'cart.add':
                  toolName = 'add_to_cart';
                  console.log('add_to_cart action received:', actionData);
                  console.log('item:', item);
                  
                  const cartItemId = extractItemId(actionData, item);
                  const cartItemName = extractItemName(actionData, item);
                  
                  if (!cartItemId) {
                    console.error('add_to_cart - no item ID found:', { actionData, item });
                    return { success: false, message: 'Item ID not found' };
                  }
                  
                  parameters = {
                    itemId: cartItemId,
                    itemName: cartItemName,
                    quantity: actionData?.quantity || actionData?.payload?.quantity || item?.quantity || 1
                  };
                  console.log('Mapped to add_to_cart:', { 
                    toolName, 
                    parameters, 
                    extractedItemId: cartItemId,
                    itemName: cartItemName,
                    fullActionData: actionData,
                    fullItem: item
                  });
                  break;
                  
                case 'view_cart':
                  toolName = 'view_cart';
                  parameters = {};
                  break;
                  
                case 'begin_checkout':
                  toolName = 'begin_checkout';
                  parameters = {};
                  break;

                case 'cart.remove':
                case 'remove_from_cart':
                  toolName = 'remove_from_cart';
                  const removeItemId = extractItemId(actionData, item);
                  const removeItemName = extractItemName(actionData, item);
                  parameters = {
                    itemId: removeItemId,
                    itemName: removeItemName
                  };
                  break;

                case 'clear_cart':
                  toolName = 'clear_cart';
                  parameters = {};
                  break;
                  
                case 'get_item_details':
                  const detailsItemId = extractItemId(actionData, item);
                  if (!detailsItemId) {
                    return { success: false, message: 'Item ID not found' };
                  }
                  toolName = 'get_item_details';
                  parameters = { itemId: detailsItemId };
                  break;
                  
                default:
                  // For unknown actions, try to pass through as-is
                  toolName = actionType;
                  parameters = actionData || item || {};
                  console.warn('Unknown widget action type:', actionType, 'Full action:', action);
              }
              
              // Only call handleToolCall if we have a valid tool name (not for product.view which is handled above)
              if (toolName) {
                console.log('Calling handleToolCall with:', { toolName, parameters });
                
                // Call the unified tool handler using component instance
                const result = await componentInstance.handleToolCall(toolName, parameters);
                
                console.log('Widget action result:', JSON.stringify(result, null, 2));
                
                // Ensure we always return a valid result
                if (result === undefined || result === null) {
                  console.warn('handleToolCall returned undefined/null, returning success object');
                  return { success: true, message: 'Action completed' };
                }
                
                return result;
              }
              
              return { success: false, message: 'Unhandled action type' };
            } catch (error) {
              console.error('Error handling widget action:', error);
              const errorMessage = error instanceof Error ? error.message : String(error);
              return { error: String(error), message: errorMessage, success: false };
            }
          };

          chatkitElement.setOptions({
            startScreen: {
              greeting: 'What can I help you find?',
              ...(startScreenConfig ? { prompts: startScreenConfig.prompts } : {}),
            },
            composer: {
              placeholder: 'What are your most popular dishes?',
            },
            widgets: {
              onAction: onActionHandler,
            },
            api: {
              getClientSecret: async (currentClientSecret?: string) => {
                try {
                  const currentTable = this.tableStore.currentTable
                    ?? await firstValueFrom(this.tableStore.currentTable$.pipe(filter(t => t !== null)));
                  const locationId = currentTable?.location?._id ?? '';
                  const tableName = currentTable?.name ?? '';
                  const tableBeaconId = currentTable?.beaconId ?? beaconId;

                  if (!currentClientSecret) {
                    // Get fresh menu context for the AI (in case menu wasn't loaded during init)
                    const menuContext = await this.chatAssistantService.getMenuContext();
                    
                    console.log('Creating ChatKit session with menu context:', 
                      menuContext.length > 100 ? `${menuContext.substring(0, 100)}...` : menuContext
                    );
                    
                    // Initial session creation with menu context
                    const response = await this.chatAssistantApi.createChatkitSession(
                      tableBeaconId,
                      menuContext,
                      locationId,
                      tableName
                    );
                    return response.client_secret;
                  }

                  // Session refresh
                  const response = await this.chatAssistantApi.refreshChatkitSession(
                    tableBeaconId,
                    currentClientSecret,
                    locationId,
                    tableName
                  );
                  return response.client_secret;
                } catch (error) {
                  console.error('Error getting client secret:', error);
                  throw error;
                }
              }
            },
            onClientTool: async (toolCall: { name: string; params?: any; arguments?: any }) => {
              // Handle tool calls from ChatKit
              console.log('ChatKit client tool call received:', toolCall);
              
              if (!toolCall || !toolCall.name) {
                console.error('Invalid tool call received:', toolCall);
                return { error: 'Invalid tool call', success: false };
              }
              
              try {
                // Handle both 'params' and 'arguments' property names
                let params = toolCall.params || toolCall.arguments || {};
                
                // Parse arguments if they're a string
                if (typeof params === 'string') {
                  try {
                    params = JSON.parse(params);
                  } catch (e) {
                    console.warn('Failed to parse tool call arguments as JSON:', e);
                  }
                }
                
                // Handle custom action format: cart.add with payload.id
                if (toolCall.name === 'cart.add' || toolCall.name === 'add_to_cart') {
                  // Extract item ID from various possible locations
                  const itemId = params.id || 
                                params.itemId || 
                                params.item_id ||
                                params.payload?.id ||
                                params.action?.payload?.id;
                  
                  if (itemId) {
                    console.log('Adding item to cart, itemId:', itemId);
                    const result = await this.handleToolCall('add_to_cart', { 
                      itemId: String(itemId), 
                      quantity: params.quantity || 1 
                    });
                    console.log('Add to cart result:', result);
                    return result;
                  } else {
                    console.error('No item ID found in cart.add params:', params);
                    return { error: 'Missing item ID', success: false };
                  }
                }
                
                // Handle other tool calls normally
                const result = await this.handleToolCall(toolCall.name, params);
                console.log('Tool call result:', result);
                return JSON.stringify(result);
              } catch (error) {
                console.error('Error executing tool call:', error);
                const errorMessage = error instanceof Error ? error.message : String(error);
                return { error: String(error), message: errorMessage, success: false };
              }
            },
          });
          
          // Verify the callback was set
          const options = chatkitElement.getOptions?.();
          console.log('ChatKit options after setOptions:', options);
          console.log('onAction handler registered:', typeof options?.widgets?.onAction === 'function');
          
          // Also try setting it directly on the element as a fallback
          if (chatkitElement.widgets) {
            chatkitElement.widgets.onAction = onActionHandler;
            console.log('Also set onAction directly on chatkitElement.widgets');
          }
          
          // Set up event listeners for tool calls (if ChatKit supports it)
          this.setupToolHandlers();
          
          // Add a DOM-level click listener to debug widget clicks
          setTimeout(() => {
            const chatContainer = document.querySelector('.chat-container');
            if (chatContainer) {
              // Listen for clicks on buttons within the chat container
              chatContainer.addEventListener('click', (event: any) => {
                const target = event.target as HTMLElement;
                // Check if it's a button or has onClickAction
                if (target.tagName === 'BUTTON' || target.closest('button')) {
                  console.log('=== Button clicked in chat container ===', {
                    target,
                    buttonText: target.textContent || target.innerText,
                    className: target.className,
                    attributes: Array.from(target.attributes).map(attr => ({ name: attr.name, value: attr.value })),
                    event
                  });
                }
              }, true); // Use capture phase to catch all clicks
              console.log('Added DOM click listener to chat container');
            }
          }, 1000);
          
          console.log('Chatkit initialized successfully with menu context');
          console.log('onAction handler is:', onActionHandler);
        } catch (error) {
          console.error('Failed to initialize Chatkit:', error);
        }
      }
      
      // Stop after max attempts
      if (attempts >= maxAttempts) {
        clearInterval(checkScriptLoaded);
        if (!this.chatkitInitialized) {
          console.warn('Chatkit initialization timeout - element or setOptions not available after', maxAttempts, 'attempts');
        }
      }
    }, 100);
  }

  /**
   * Set up handlers for tool calls from the AI
   * These will be called when the AI wants to perform actions like searching menu, adding to cart, etc.
   */
  private setupToolHandlers(): void {
    console.log('Tool handlers ready for ChatKit function calls');
  }

  /**
   * Public method to handle tool calls from the backend
   * This can be called by the ChatKit element or through event listeners
   */
  async handleToolCall(toolName: string, parameters: any): Promise<any> {
    console.log(`Handling tool call: ${toolName}`, parameters);

    try {
      switch (toolName) {
        case 'search_menu':
          return await this.chatAssistantService.searchMenuItems(parameters.query);

        case 'get_item_details':
          return await this.chatAssistantService.getItemDetails(parameters.itemId);

        case 'get_categories':
          return await this.chatAssistantService.getCategories();

        case 'get_items_by_category':
          return await this.chatAssistantService.getItemsByCategory(parameters.categoryName);

        case 'get_dietary_items':
          return await this.chatAssistantService.getItemsByDietaryTag(parameters.tag);

        case 'add_to_cart':
          const result = await this.chatAssistantService.addItemToCart(
            parameters.itemId,
            parameters.quantity || 1,
            parameters.itemName // Pass item name for fallback lookup
          );
          
          // If item requires modifiers, it navigates to item details - close chat window
          if (result.requiresModifiers) {
            this.chatAssistantStore.setChatModalOpen(false);
          }
          
          // If successful, optionally show confirmation in UI
          if (result.success && !result.requiresModifiers) {
            // Could trigger a toast or visual confirmation here
            console.log('Item added to cart successfully');
          }
          
          return result;

        case 'get_cart_summary':
          return await this.chatAssistantService.getCartSummary();

        case 'remove_from_cart':
          return await this.chatAssistantService.removeFromCart(
            parameters.itemId,
            parameters.itemName || parameters.name
          );

        case 'clear_cart':
          return await this.chatAssistantService.clearCart();

        case 'view_cart':
          await this.chatAssistantService.viewCart();
          // Close chat modal when navigating
          this.chatAssistantStore.setChatModalOpen(false);
          return { success: true };

        case 'begin_checkout':
          const checkoutResult = await this.chatAssistantService.beginCheckout();
          if (checkoutResult.success) {
            // Close chat modal when navigating to checkout
            this.chatAssistantStore.setChatModalOpen(false);
          }
          return checkoutResult;

        case 'request_drink_refill':
          const beaconId = this.tableStore.currentTable?.beaconId;
          if (!beaconId) {
            return { success: false, message: 'Table not found. Please scan the table QR code first.' };
          }
          try {
            const refillResult = await this.chatAssistantApi.requestDrinkRefill(
              beaconId,
              parameters.drinkType
            );
            return refillResult?.success
              ? { success: true, message: 'Your server has been notified about the drink refill.' }
              : { success: false, message: 'Could not send the refill request. Please try again.' };
          } catch (err) {
            console.error('request_drink_refill failed:', err);
            return { success: false, message: 'Could not send the refill request. Please try again.' };
          }

        default:
          console.warn(`Unknown tool call: ${toolName}`);
          return { error: `Unknown tool: ${toolName}` };
      }
    } catch (error) {
      console.error(`Error handling tool call ${toolName}:`, error);
      return { error: String(error) };
    }
  }
}
